import { useState, useMemo, useCallback } from 'react';
import {
  Loader2,
  Package,
  AlertCircle,
  Search,
  Barcode,
  ScanLine,
  ChevronRight,
  X,
  Building2,
  Users,
  Plus,
} from 'lucide-react';
import { Header } from '@/components/Header';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useWarehouse } from './hooks/useWarehouse';
import { BarcodeScanner } from '@/components/BarcodeScanner';
import { WarehouseItemSheet } from './components/WarehouseItemSheet';
import { CreateWarehouseItemSheet } from './components/CreateWarehouseItemSheet';
import type { WarehouseItem } from '@/api/warehouse';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const WAREHOUSE_BARCODE_PATTERN = /^DS[0-9A-Z]{6}$/;

function normalizeBarcode(value: string): string {
  return value.trim().toUpperCase();
}

function WarehouseItemCard({
  item,
  isMine,
  onClick,
}: {
  item: WarehouseItem;
  isMine: boolean;
  onClick: () => void;
}) {
  const isAvailable = item.isInOffice;
  const holderName = item.assignedToName || item.legacyHolderName || 'сотрудника';

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full p-3 rounded-lg border bg-card border-border text-left transition-colors hover:bg-accent active:bg-accent"
    >
      <div className="flex items-start justify-between mb-1">
        <span className="font-medium text-sm flex-1 pr-2">{item.name}</span>
        <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
      </div>

      {item.barcode && (
        <div className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
          <Barcode className="w-3 h-3" />
          {item.barcode}
        </div>
      )}

      {item.comment && (
        <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
          {item.comment}
        </p>
      )}

      <div className="flex items-center gap-2">
        {isAvailable ? (
          <Badge
            variant="outline"
            className="bg-green-50 text-green-700 border-green-300 text-xs"
          >
            На складе
          </Badge>
        ) : isMine ? (
          <Badge
            variant="outline"
            className="bg-blue-50 text-blue-700 border-blue-300 text-xs"
          >
            У меня
          </Badge>
        ) : (
          <Badge
            variant="outline"
            className="bg-gray-50 text-gray-600 border-gray-300 text-xs"
          >
            У {holderName}
          </Badge>
        )}

        {item.clientName && (
          <span className="text-xs text-blue-600">@ {item.clientName}</span>
        )}
      </div>
    </button>
  );
}

function WarehouseSection({
  title,
  items,
  searchQuery,
  emptyMessage,
  onItemClick,
  isMine,
}: {
  title: string;
  items: WarehouseItem[];
  searchQuery: string;
  emptyMessage: string;
  onItemClick: (item: WarehouseItem, isMine: boolean) => void;
  isMine: boolean;
}) {
  const filteredItems = searchQuery
    ? items.filter(
        (item) =>
          item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.barcode?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.comment?.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : items;

  return (
    <div className="mb-6">
      <h2 className="font-semibold text-sm text-muted-foreground mb-3 uppercase tracking-wide">
        {title} ({filteredItems.length})
      </h2>

      {filteredItems.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">
          {emptyMessage}
        </p>
      ) : (
        <div className="space-y-2">
          {filteredItems.map((item) => (
            <WarehouseItemCard
              key={item.id}
              item={item}
              isMine={isMine}
              onClick={() => onItemClick(item, isMine)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function WarehousePage() {
  const { data, isLoading, error } = useWarehouse();
  const [searchQuery, setSearchQuery] = useState('');
  const [scannerOpen, setScannerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<WarehouseItem | null>(null);
  const [selectedIsMine, setSelectedIsMine] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createInitialBarcode, setCreateInitialBarcode] = useState<string | undefined>();
  const [showOthers, setShowOthers] = useState(true);
  const [showAvailable, setShowAvailable] = useState(true);

  // Combine all items for barcode search
  const allItems = useMemo(() => {
    if (!data) return [];
    return [...(data.my || []), ...(data.available || []), ...(data.others || [])];
  }, [data]);

  // My item IDs for quick lookup
  const myItemIds = useMemo(() => {
    if (!data?.my) return new Set<string>();
    return new Set(data.my.map((i) => i.id));
  }, [data?.my]);

  const handleItemClick = useCallback((item: WarehouseItem, isMine: boolean) => {
    setSelectedItem(item);
    setSelectedIsMine(isMine);
    setSheetOpen(true);
  }, []);

  const handleBarcodeScan = useCallback(
    (barcode: string) => {
      setScannerOpen(false);
      const normalizedBarcode = normalizeBarcode(barcode);

      // Find item by barcode
      const matches = allItems.filter(
        (i) => i.barcode?.toUpperCase() === normalizedBarcode,
      );

      if (matches.length === 1) {
        const item = matches[0];
        setSelectedItem(item);
        setSelectedIsMine(myItemIds.has(item.id));
        setSheetOpen(true);
      } else if (matches.length > 1) {
        setSearchQuery(normalizedBarcode);
        toast.warning('Найдено несколько товаров', {
          description: `Штрихкод: ${normalizedBarcode}`,
        });
      } else {
        setSearchQuery(normalizedBarcode);
        if (WAREHOUSE_BARCODE_PATTERN.test(normalizedBarcode)) {
          setCreateInitialBarcode(normalizedBarcode);
          setCreateOpen(true);
          toast.info('Товар не найден, можно добавить новую позицию', {
            description: `Штрихкод: ${normalizedBarcode}`,
          });
        } else {
          toast.error('Товар не найден', {
            description: `Штрихкод: ${normalizedBarcode}`,
          });
        }
      }
    },
    [allItems, myItemIds],
  );

  const openCreate = () => {
    setCreateInitialBarcode(undefined);
    setCreateOpen(true);
  };

  const othersCount = data?.others?.length ?? 0;
  const availableCount = data?.available?.length ?? 0;

  return (
    <div className="min-h-screen bg-background">
      <Header
        title="Склад"
        showBack
        rightContent={
          <>
            <Button
              variant="ghost"
              size="icon"
              onClick={openCreate}
              className="h-9 w-9"
              aria-label="Добавить товар"
            >
              <Plus className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setScannerOpen(true)}
              className="h-9 w-9"
              aria-label="Сканировать штрихкод"
            >
              <ScanLine className="h-5 w-5" />
            </Button>
          </>
        }
      />

      <main className="p-4">
        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Поиск по названию или штрихкоду..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-10"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Filter chips */}
        {!isLoading && !error && data && (
          <div className="flex gap-2 mb-4">
            <button
              type="button"
              onClick={() => setShowOthers(!showOthers)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
                showOthers
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              )}
            >
              <Users className="w-3.5 h-3.5" />
              У коллег ({othersCount})
            </button>
            <button
              type="button"
              onClick={() => setShowAvailable(!showAvailable)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
                showAvailable
                  ? 'bg-green-600 text-white'
                  : 'bg-muted text-muted-foreground'
              )}
            >
              <Building2 className="w-3.5 h-3.5" />
              В офисе ({availableCount})
            </button>
          </div>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle className="w-12 h-12 text-destructive mb-4" />
            <p className="text-muted-foreground">Ошибка загрузки</p>
            <p className="text-sm text-muted-foreground mt-1">
              {error instanceof Error ? error.message : 'Попробуйте позже'}
            </p>
          </div>
        )}

        {/* Content */}
        {!isLoading && !error && data && (
          <>
            <WarehouseSection
              title="Моё оборудование"
              items={data.my || []}
              searchQuery={searchQuery}
              emptyMessage="У вас нет оборудования"
              onItemClick={handleItemClick}
              isMine={true}
            />

            {showOthers && othersCount > 0 && (
              <WarehouseSection
                title="У других сотрудников"
                items={data.others || []}
                searchQuery={searchQuery}
                emptyMessage=""
                onItemClick={handleItemClick}
                isMine={false}
              />
            )}

            {showAvailable && (
              <WarehouseSection
                title="Доступно в офисе"
                items={data.available || []}
                searchQuery={searchQuery}
                emptyMessage="Нет доступного оборудования"
                onItemClick={handleItemClick}
                isMine={false}
              />
            )}
          </>
        )}

        {/* Empty state when no data */}
        {!isLoading && !error && !data && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Package className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Склад пуст</p>
          </div>
        )}
      </main>

      {/* Barcode Scanner */}
      {scannerOpen && (
        <BarcodeScanner
          onScan={handleBarcodeScan}
          onClose={() => setScannerOpen(false)}
        />
      )}

      {/* Item Detail Sheet */}
      <WarehouseItemSheet
        item={selectedItem}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        isMine={selectedIsMine}
      />

      <CreateWarehouseItemSheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        initialBarcode={createInitialBarcode}
        onCreated={(item) => {
          setSelectedItem(item);
          setSelectedIsMine(false);
          setSheetOpen(true);
        }}
      />
    </div>
  );
}
